// GA4 / GTM dataLayer tracking for the Moisturizer PLP (/collections/moisturizers) — the
// "Dragon Fruit Banner test" CRO experiment (Convert). Per the agency's "E-commerce for
// Dragon Fruit Banner test" spec, Mweb only:
//
//   view_item_list    product cards at least 50% on screen. Batched — the cards that come into
//                     view together go in one push — and each product once per page load.
//                     The Dragon Fruit banner is never included.
//   select_item       click on a product card's image or title. Original only (see
//                     SELECT_ITEM_ORIGINAL_ONLY).
//   view_promotion    the Dragon Fruit banner at least 50% on screen. Once per page load.
//   select_promotion  click on the Dragon Fruit banner.
//
// THE BANNER
// The variant's Convert code keeps the theme's card wrapper (data-product-card,
// data-product-id) for the Dragon Fruit product, swaps its contents for one image link, and
// tags it data-cv-replaced="true". That tag is what tells banner from product card, and
// whether the visitor is in the variant at all.
//
// PRODUCT DATA
// Nothing is added to the theme markup for this. The card supplies what it shows: title, the
// price and strikethrough price as displayed, and the variant picked (its add-to-cart form's
// id input). SKU, product type, stock and option names come from the storefront —
// /collections/<handle>/products.json, fetched once on load, with /products/<handle>.js for
// any product it does not cover.
//
// item_list_id / item_list_name: the collection (URL handle / page heading); a sort the user
// picks is added to the name ("Moisturizers | Best selling"). index: the card's 1-based slot
// in the grid.
//
// sections/collection-grid.liquid loads this on every collection, so it stops straight away
// anywhere but TRACKED_COLLECTION.
(() => {
  'use strict';

  // Only the collection page itself: /collections/moisturizers (query string such as
  // ?sort_by= or Convert's preview params allowed). Not a tag-filtered URL
  // (/collections/moisturizers/<tag>), a product opened through the collection
  // (/collections/moisturizers/products/<handle>), or any other collection.
  const TRACKED_PATH = /^\/collections\/moisturizers\/?$/i;
  const pathname = window.location.pathname.replace(/^\/[a-z]{2}(-[a-z]{2})?(?=\/collections\/)/i, '');
  if (!TRACKED_PATH.test(pathname)) return;

  const SECTION_SELECTOR      = '[data-cg-section]';
  const GRID_SELECTOR         = '[data-cg-grid]';
  const CARD_SELECTOR         = '[data-product-card]';
  const BANNER_SELECTOR       = '[data-product-card][data-cv-replaced="true"]';
  const PRODUCT_LINK_SELECTOR = 'a[href*="/products/"]';

  const PROMOTION = {
    id:   'dragon_fruit_banner',
    name: 'Dragon Fruit Banner',
    // The only banner on the page.
    slot: 1,
  };

  // The spec puts select_item on the original only; in the variant only the banner's
  // select_promotion is sent. Set to false to send select_item in both.
  const SELECT_ITEM_ORIGINAL_ONLY = true;

  const BRAND = 'Dot & Key';
  const DEFAULT_LIST = { id: 'moisturizers', name: 'Moisturizers' };
  const VISIBLE_RATIO = 0.5;
  // Cards that cross into view within this window go out as one view_item_list.
  const IMPRESSION_BATCH_MS = 300;
  // Longest an impression waits on the product data before pushing with what it has.
  const RESOLVE_TIMEOUT_MS = 3000;

  // Mobile is anything below the theme's md breakpoint (768px, tailwind.config.js). Read at
  // event time, so a rotation or resized window is respected.
  const MWEB_QUERY = '(max-width: 767px)';
  const isMweb = () => window.matchMedia(MWEB_QUERY).matches;

  // ── Shared utilities (window.DnkTracking) ───────────────────────────────────
  // Same helpers, and same fallbacks, as last-visited-pdp-tracking.js so client_id agrees
  // across every event.
  const T = (window.DnkTracking = window.DnkTracking || {});

  if (!T.getClientId) {
    const CLIENT_ID_STORAGE_KEY = 'themex-client-id';
    T.getClientId = () => {
      const gaCookie = document.cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
      if (gaCookie) {
        const parts = decodeURIComponent(gaCookie[1]).split('.');
        if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
      }
      let clientId = localStorage.getItem(CLIENT_ID_STORAGE_KEY);
      if (!clientId) {
        clientId = crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        localStorage.setItem(CLIENT_ID_STORAGE_KEY, clientId);
      }
      return clientId;
    };
  }

  // A malformed _ga cookie or blocked localStorage must not stop the push.
  const getClientId = () => {
    try {
      return T.getClientId();
    } catch (_) {
      return null;
    }
  };

  const getConvertVariant = () => {
    try {
      const convert     = window.convert;
      const experiences = convert && convert.currentData && convert.currentData.experiences;
      if (!experiences) return null;

      const definitions = (convert.data && convert.data.experiences) || [];
      const values = Object.keys(experiences)
        .map((experienceId) => {
          const variation = experiences[experienceId] && experiences[experienceId].variation;
          if (!variation) return null;
          const definition = Array.isArray(definitions)
            ? definitions.find((experience) => String(experience.id) === String(experienceId))
            : definitions[experienceId];
          const experienceName = (definition && definition.name) || experienceId;
          const variationName  = variation.name || variation.id;
          return variationName ? `${experienceName} | ${variationName}` : null;
        })
        .filter(Boolean);

      return values.length ? values.join(', ') : null;
    } catch (_) {
      return null;
    }
  };

  const getExpVariantString = () => window.dnkExperimentVariant || getConvertVariant() || null;

  const pushEvent = (payload) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({ ...payload, client_id: getClientId() });
  };

  // ── List context ────────────────────────────────────────────────────────────
  const readCollectionHandle = () => {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : DEFAULT_LIST.id;
  };

  const readSortLabel = () => {
    const sortBy = new URL(window.location.href).searchParams.get('sort_by');
    const defaultSort = document.querySelector('[data-default-sort]')?.dataset.defaultSort;
    if (!sortBy || sortBy === defaultSort) return null;

    const option = [...document.querySelectorAll('[data-sort-option]')].find(
      (button) => button.dataset.sortValue === sortBy
    );
    return option?.querySelector('span')?.textContent.trim() || sortBy;
  };

  // Read at event time — a sort re-renders the grid in place (collection-grid.js), so the list
  // a product was seen or clicked in is the one on screen at that moment.
  const readList = () => {
    const title =
      document.querySelector(`${SECTION_SELECTOR} h1`)?.textContent.trim() || DEFAULT_LIST.name;
    const sortLabel = readSortLabel();
    return {
      id:   readCollectionHandle(),
      name: sortLabel ? `${title} | ${sortLabel}` : title,
    };
  };

  // Slot among every card in the grid, counted from 1 — the banner keeps the Dragon Fruit
  // card's slot, and the quiz tile is not a card, so neither shifts the others.
  const readIndex = (card) => {
    const grid = card.closest(GRID_SELECTOR);
    if (!grid) return 1;
    const position = [...grid.querySelectorAll(CARD_SELECTOR)].indexOf(card);
    return position >= 0 ? position + 1 : 1;
  };

  const readHandle = (card) => {
    const href = card.querySelector(PRODUCT_LINK_SELECTOR)?.getAttribute('href') || '';
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  // ── Product data from the storefront ────────────────────────────────────────
  // Both endpoints are normalised to one shape, prices in rupees:
  //   { title, type, optionNames, variants: [{ id, sku, available, options, price, compareAt }] }
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const toRupees = (value, inPaise) => {
    if (value == null || value === '') return null;
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    return inPaise ? Math.round(number) / 100 : number;
  };

  // products.json: prices are rupee strings, options are option1..3, options are { name }.
  // /products/<handle>.js: prices are paise, options is an array, option names are strings
  // (or { name } objects on newer storefronts).
  const normalizeProduct = (product, pricesInPaise) => ({
    id:          String(product.id),
    title:       product.title || null,
    type:        product.product_type || product.type || null,
    optionNames: (product.options || []).map((option) =>
      typeof option === 'string' ? option : option?.name
    ),
    variants: (product.variants || []).map((variant) => ({
      id:        String(variant.id),
      sku:       variant.sku || null,
      available: variant.available !== false,
      options:   variant.options || [variant.option1, variant.option2, variant.option3].filter((o) => o != null),
      price:     toRupees(variant.price, pricesInPaise),
      compareAt: toRupees(variant.compare_at_price, pricesInPaise),
    })),
  });

  const productsById = new Map();
  const productLookups = new Map();

  const catalog = fetch(`${root()}collections/${encodeURIComponent(readCollectionHandle())}/products.json?limit=250`)
    .then((response) => (response.ok ? response.json() : null))
    .then((json) => {
      (json?.products || []).forEach((product) => {
        const normalized = normalizeProduct(product, false);
        productsById.set(normalized.id, normalized);
      });
    })
    .catch(() => {});

  // Resolves to the normalised product, or null; never rejects.
  const resolveProduct = (productId, handle) =>
    catalog.then(() => {
      if (productId && productsById.has(String(productId))) return productsById.get(String(productId));
      if (!handle) return null;
      if (!productLookups.has(handle)) {
        productLookups.set(
          handle,
          fetch(`${root()}products/${encodeURIComponent(handle)}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .then((product) => {
              if (!product) return null;
              const normalized = normalizeProduct(product, true);
              productsById.set(normalized.id, normalized);
              return normalized;
            })
            .catch(() => null)
        );
      }
      return productLookups.get(handle);
    });

  // Synchronous read (for clicks, which cannot wait on a lookup).
  const knownProduct = (productId) => productsById.get(String(productId)) || null;

  const withTimeout = (promise, ms) =>
    Promise.race([promise, new Promise((resolve) => setTimeout(() => resolve(null), ms))]);

  // ── Item payload ────────────────────────────────────────────────────────────
  // "Option | Value", options joined with " : " (spec); NA for a default-only product.
  const formatVariant = (optionNames, values) => {
    if (!values?.length || (values.length === 1 && values[0] === 'Default Title')) return 'NA';
    return values.map((value, i) => `${optionNames[i] || 'Option'} | ${value}`).join(' : ');
  };

  const buildItem = (fields, list) => ({
    item_id:             fields.itemId,
    item_name:           fields.name,
    index:               fields.index,
    item_brand:          BRAND,
    item_category:       fields.category || 'NA',
    item_category2:      'NA',
    item_category3:      'NA',
    item_category4:      'NA',
    item_category5:      'NA',
    item_list_id:        list.id,
    item_list_name:      list.name,
    item_variant:        fields.variant || 'NA',
    price:               fields.price,
    item_original_price: fields.originalPrice != null ? fields.originalPrice : fields.price,
    item_status:         fields.available ? 'in-stock' : 'out-of-stock',
  });

  // Fields for one variant of a product; `variantId` falls back to the first available.
  const productFields = (product, variantId) => {
    const variants = product?.variants || [];
    const variant =
      variants.find((v) => variantId && v.id === String(variantId)) ||
      variants.find((v) => v.available) ||
      variants[0] ||
      null;
    return {
      itemId:        (variant?.sku && variant.sku.trim()) || variant?.id || product?.id || null,
      name:          product?.title || null,
      category:      product?.type || null,
      variant:       variant ? formatVariant(product.optionNames, variant.options) : 'NA',
      price:         variant ? variant.price : null,
      originalPrice: variant && variant.compareAt > variant.price ? variant.compareAt : variant ? variant.price : null,
      available:     variant ? variant.available : true,
    };
  };

  // ── Product cards ───────────────────────────────────────────────────────────
  const parsePrice = (text) => {
    const match = String(text || '').replace(/,/g, '').match(/\d+(\.\d+)?/);
    return match ? Number(match[0]) : null;
  };

  // The storefront data gives the ids; the card gives what the shopper sees — its title and
  // the price / strikethrough price as displayed (variant discounts are applied in the theme,
  // not in the storefront price).
  const readCardFields = (card, product) => {
    const variantId = card.querySelector('input[name="id"]')?.value || null;
    const fields = productFields(product, variantId);

    const price = parsePrice(card.querySelector('[data-product-card-price]')?.textContent);
    const compareEl = card.querySelector('[data-product-card-compare-price]');
    const comparePrice = compareEl && !compareEl.classList.contains('hidden')
      ? parsePrice(compareEl.textContent)
      : null;

    return {
      ...fields,
      itemId:        fields.itemId || variantId || card.dataset.productId || null,
      name:          card.querySelector('[data-product-card-title-link]')?.textContent.trim() || fields.name,
      index:         readIndex(card),
      price:         price != null ? price : fields.price,
      originalPrice: comparePrice != null ? comparePrice : price != null ? price : fields.originalPrice,
    };
  };

  // ── The banner ──────────────────────────────────────────────────────────────
  const readBannerFields = (card, product) => {
    const link = card.querySelector(PRODUCT_LINK_SELECTOR);
    const fields = productFields(product, null);
    return {
      ...fields,
      itemId: fields.itemId || card.dataset.productId || null,
      name:   fields.name || link?.getAttribute('aria-label')?.trim() || link?.querySelector('img')?.getAttribute('alt')?.trim() || null,
      index:  readIndex(card),
    };
  };

  const pushPromotion = (eventName, card, product) => {
    const list = readList();
    pushEvent({
      event:              eventName,
      exp_variant_string: getExpVariantString(),
      ecommerce: {
        promotion_id:   PROMOTION.id,
        promotion_name: PROMOTION.name,
        creative_name:  list.name,
        creative_slot:  PROMOTION.slot,
        items:          [buildItem(readBannerFields(card, product), list)],
      },
    });
  };

  // ── view_promotion ──────────────────────────────────────────────────────────
  let promotionViewed = false;

  const trackPromotionView = async (card) => {
    if (promotionViewed) return;
    promotionViewed = true;
    const product = await withTimeout(
      resolveProduct(card.dataset.productId, readHandle(card)),
      RESOLVE_TIMEOUT_MS
    );
    pushPromotion('view_promotion', card, product);
  };

  // ── view_item_list ──────────────────────────────────────────────────────────
  // Keyed on the product, so a card re-rendered by a sort or by Convert is not a second
  // impression on the same page load.
  const impressed = new Set();
  let pending = [];
  let flushTimer = null;

  // `immediate` (page being left): push now with whatever product data has already loaded —
  // there is no time to wait on a lookup before the page unloads.
  const flushImpressions = async (immediate = false) => {
    clearTimeout(flushTimer);
    flushTimer = null;

    // The banner is checked again here: Convert may have tagged the card after it was queued.
    // impressed is updated inside the filter so a product shown on two cards in the same batch
    // is only sent once.
    const cards = pending.filter((card) => {
      const key = card.dataset.productId;
      if (!card.isConnected || card.matches(BANNER_SELECTOR) || impressed.has(key)) return false;
      impressed.add(key);
      return true;
    });
    pending = [];
    if (!cards.length) return;

    const products = immediate
      ? null
      : await withTimeout(
          Promise.all(cards.map((card) => resolveProduct(card.dataset.productId, readHandle(card)))),
          RESOLVE_TIMEOUT_MS
        );

    const list = readList();
    const items = cards
      .map((card, i) => buildItem(readCardFields(card, products ? products[i] : knownProduct(card.dataset.productId)), list))
      .sort((a, b) => a.index - b.index);

    pushEvent({
      event: 'view_item_list',
      ecommerce: {
        item_list_id:   list.id,
        item_list_name: list.name,
        items,
      },
    });
  };

  const queueImpression = (card) => {
    if (!card.dataset.productId) return;
    pending.push(card);
    if (!flushTimer) flushTimer = setTimeout(flushImpressions, IMPRESSION_BATCH_MS);
  };

  // ── Visibility ──────────────────────────────────────────────────────────────
  const onVisible = (card) => {
    if (card.matches(BANNER_SELECTOR)) {
      trackPromotionView(card);
    } else {
      queueImpression(card);
    }
  };

  // A card with no height yet — the banner's <img> has no width/height, so it is ~0px tall
  // until the image downloads — is reported by the browser as 100% visible whenever it touches
  // the viewport. It is ignored until it has real height, and re-checked once its image loads
  // (re-observing makes the browser report the card's current visibility afresh).
  const recheckWhenImageLoads = (card) => {
    const image = card.querySelector('img');
    if (!image || image.complete || image.dataset.dnkTrackRecheck) return;
    image.dataset.dnkTrackRecheck = 'true';
    image.addEventListener(
      'load',
      () => {
        visibilityObserver.unobserve(card);
        visibilityObserver.observe(card);
      },
      { once: true }
    );
  };

  // On desktop a card stays observed rather than being dropped, so a window narrowed to
  // mobile width later still gets its impression.
  //
  // Several thresholds rather than just 0.5: the browser only reports a card when its visible
  // share crosses one, so a card growing or shrinking (image loading in, layout shifts) is
  // still re-reported on its way past 50%.
  const visibilityObserver =
    typeof IntersectionObserver === 'undefined'
      ? null
      : new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || entry.boundingClientRect.height < 1) {
                if (entry.isIntersecting) recheckWhenImageLoads(entry.target);
                return;
              }
              if (entry.intersectionRatio < VISIBLE_RATIO || !isMweb()) return;
              visibilityObserver.unobserve(entry.target);
              onVisible(entry.target);
            });
          },
          { threshold: [0, 0.25, VISIBLE_RATIO, 0.75, 1] }
        );

  const observedCards = new WeakSet();

  const observeCard = (card) => {
    if (observedCards.has(card)) return;
    observedCards.add(card);

    // Without IntersectionObserver there is no way to tell when a card is on screen, so it is
    // treated as seen straight away rather than never.
    if (!visibilityObserver) {
      if (isMweb()) onVisible(card);
      return;
    }
    visibilityObserver.observe(card);
  };

  const scanForCards = () => {
    document.querySelectorAll(`${GRID_SELECTOR} ${CARD_SELECTOR}`).forEach(observeCard);

    // The banner's product is looked up the moment Convert tags it, ready for a click. A card
    // tagged after it was already seen as a product card is watched again for its promotion.
    document.querySelectorAll(BANNER_SELECTOR).forEach((card) => {
      resolveProduct(card.dataset.productId, readHandle(card));
      if (!promotionViewed && visibilityObserver) visibilityObserver.observe(card);
    });
  };

  // ── select_item + select_promotion ──────────────────────────────────────────
  // Capture phase so the push runs ahead of the navigation the link starts — which is also why
  // it cannot wait on a lookup and uses whatever has already loaded. GTM's GA4 tag sends by
  // beacon, so the hit survives the page unload.
  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.(PRODUCT_LINK_SELECTOR);
      const card = link?.closest(CARD_SELECTOR);
      if (!card || !card.closest(GRID_SELECTOR) || !isMweb()) return;

      const product = knownProduct(card.dataset.productId);

      if (card.matches(BANNER_SELECTOR)) {
        pushPromotion('select_promotion', card, product);
        return;
      }

      if (SELECT_ITEM_ORIGINAL_ONLY && document.querySelector(BANNER_SELECTOR)) return;

      const list = readList();
      pushEvent({
        event:              'select_item',
        exp_variant_string: getExpVariantString(),
        ecommerce: {
          item_list_id:   list.id,
          item_list_name: list.name,
          items:          [buildItem(readCardFields(card, product), list)],
        },
      });
    },
    true
  );

  // Whatever is still queued goes out before the page is left.
  window.addEventListener('pagehide', () => flushImpressions(true));

  // ── Init ────────────────────────────────────────────────────────────────────
  // Convert can tag the banner at any point after load, and infinite scroll and sorting add
  // or replace cards, so the section is watched for both.
  const init = () => {
    scanForCards();
    const target = document.querySelector(SECTION_SELECTOR) || document.body;
    new MutationObserver(scanForCards).observe(target, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-cv-replaced'],
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
